import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, Loader2, PlugZap, Send, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const Etape = ({ ok, titre, detail, testid }) => (
  <div className="flex items-start gap-2" data-testid={testid}>
    {ok ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" /> : <Circle className="w-4 h-4 text-stone-300 mt-0.5" />}
    <div><p className="text-sm font-medium">{titre}</p>{detail && <p className="text-xs text-muted-foreground">{detail}</p>}</div>
  </div>
);

function FormIdentifiants({ plateforme, onFait }) {
  const [f, setF] = useState({ api_url: '', client_id: '', client_secret: '' });
  const [envoi, setEnvoi] = useState(false);
  const valider = async () => {
    setEnvoi(true);
    try {
      const r = await api.post('/facturation-electronique/pa/connexion/identifiants', { plateforme, ...f });
      r.data.connexion?.api_ok ? toast.success('Plateforme connectée : les factures partent toutes seules') : toast.error('Identifiants refusés par la plateforme');
      onFait(r.data);
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Erreur'); }
    setEnvoi(false);
  };
  return (
    <div className="grid md:grid-cols-4 gap-2 pt-2" data-testid="pa-form-identifiants">
      <Input placeholder="Adresse de l'API" value={f.api_url} onChange={(e) => setF({ ...f, api_url: e.target.value })} data-testid="pa-api-url" />
      <Input placeholder="Identifiant client" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} data-testid="pa-client-id" />
      <Input placeholder="Secret" type="password" value={f.client_secret} onChange={(e) => setF({ ...f, client_secret: e.target.value })} data-testid="pa-client-secret" />
      <Button onClick={valider} disabled={envoi || !f.api_url || !f.client_id || !f.client_secret} data-testid="pa-enregistrer-identifiants">
        {envoi && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Connecter
      </Button>
    </div>
  );
}

export function ConnexionPlateforme() {
  const [etat, setEtat] = useState(null);
  const [charge, setCharge] = useState(false);
  const charger = useCallback(() => api.get('/facturation-electronique/pa/connexion').then((r) => setEtat(r.data)).catch(() => {}), []);
  useEffect(() => { charger(); }, [charger]);
  const detecter = async () => {
    setCharge(true);
    try {
      const r = await api.post('/facturation-electronique/pa/connexion/auto');
      setEtat(r.data);
      r.data.connexion?.plateforme ? toast.success(`Plateforme trouvée : ${r.data.connexion.plateforme_nom}`) : toast.warning('Aucune plateforme trouvée dans les mails de cette société');
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Détection impossible'); }
    setCharge(false);
  };
  const demander = async () => {
    try { toast.success((await api.post('/facturation-electronique/pa/connexion/demande-acces')).data.message); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Erreur'); }
  };
  const basculer = async (actif) => setEtat((await api.post('/facturation-electronique/pa/connexion/envoi-auto', { actif })).data);
  if (!etat) return <Card data-testid="pa-connexion-chargement"><CardContent className="p-5 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Connexion plateforme agréée : vérification…</CardContent></Card>;
  const c = etat.connexion || {};
  return (
    <Card className="border-green-200" data-testid="pa-connexion-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <PlugZap className="w-5 h-5 text-green-700" />
            <h2 className="font-semibold">Connexion plateforme agréée · {etat.societe_nom}</h2>
            {c.api_ok && <span className="text-xs bg-green-100 text-green-800 rounded-full px-2 py-0.5" data-testid="pa-statut-connectee">Connectée</span>}
          </div>
          <Button size="sm" variant="outline" onClick={detecter} disabled={charge} data-testid="pa-detecter">
            {charge ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}Connexion automatique
          </Button>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <Etape ok={!!etat.siren} titre="SIREN" detail={etat.siren || 'à compléter dans la fiche société'} testid="pa-etape-siren" />
          <Etape ok={!!c.plateforme} titre="Plateforme choisie" detail={c.plateforme_nom || 'non détectée'} testid="pa-etape-plateforme" />
          <Etape ok={!!c.annuaire_inscrit} titre="Inscrite à l'annuaire" detail={c.accord_signe ? 'accord de choix signé' : ''} testid="pa-etape-annuaire" />
          <Etape ok={!!c.api_ok} titre="Envoi automatique branché" detail={c.api_ok ? 'les factures partent seules' : 'accès API à recevoir'} testid="pa-etape-api" />
        </div>
        {c.plateforme && !c.api_ok && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid="pa-attente-api">
            <p className="text-sm">Dernière étape : la plateforme doit nous donner un accès API. Une fois saisi, c'est réglé pour toujours.
              {etat.factures_en_attente > 0 && <b> {etat.factures_en_attente} facture(s) partiront dès la connexion.</b>}</p>
            <Button size="sm" variant="secondary" onClick={demander} data-testid="pa-demander-acces"><Send className="w-4 h-4 mr-2" />Faire demander l'accès par Valentine</Button>
            <FormIdentifiants plateforme={c.plateforme} onFait={setEtat} />
          </div>
        )}
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={c.envoi_auto !== false} onCheckedChange={basculer} data-testid="pa-envoi-auto" />
          Chaque facture validée dans « Devis & Factures » devient électronique et part toute seule
        </label>
      </CardContent>
    </Card>
  );
}
